/*
 * native-slider.js — tiny dependency-free 1-per-view slider used by the custom
 * autoplay sections (replaces Swiper). Progressive enhancement over a native
 * CSS scroll-snap track: swipe already works with zero JS; this adds autoplay,
 * pagination bullets, prev/next buttons, an optional progress bar, and a
 * seamless infinite loop (1,2,3,1,2,3... forward and 3,2,1,3,2,1... reverse).
 *
 * Markup contract on the root element (add `data-native-slider`):
 *   [data-native-slider]           the root (holds pagination / nav as children)
 *     .swiper-wrapper              the horizontal scroll-snap track
 *       .swiper-slide  x N         each 100%/view wide (set in the section CSS)
 *     .swiper-pagination           (optional) bullets are generated into it
 *     .swiper-button-prev/next     (optional) wired if present
 *     [data-native-progress]       (optional) fill element for the progress bar
 *
 * Options via attributes: data-autoplay="<ms>" (0/absent = off), data-loop="true".
 *
 * Loop implementation: when data-loop="true" and there are 2+ real slides, a
 * clone of the last slide is prepended and a clone of the first slide is
 * appended to the track. The slider always sits on a "real" slide; scrolling
 * forward past the last real slide smoothly lands on the clone-of-first, then
 * the code instantly (no animation) re-centers on the real first slide before
 * the user can see the jump — and symmetrically in reverse. This gives a
 * visually seamless loop without ever needing to animate backwards across the
 * whole track.
 */
(function () {
  function init(root) {
    if (root.dataset.nsBound) return;
    var track = root.querySelector('.swiper-wrapper');
    if (!track) return;
    var realSlides = Array.prototype.slice.call(track.querySelectorAll('.swiper-slide'));
    if (realSlides.length < 2) return; // single slide: nothing to do
    root.dataset.nsBound = 'true';

    var delay = parseInt(root.getAttribute('data-autoplay'), 10) || 0;
    var loop = root.getAttribute('data-loop') === 'true' && realSlides.length > 1;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var slides = realSlides; // may be replaced with [cloneLast, ...real, cloneFirst] below
    var offset = 0;          // index shift between "real" index and DOM index, when looping
    var headClone, tailClone;

    if (loop) {
      headClone = realSlides[realSlides.length - 1].cloneNode(true);
      tailClone = realSlides[0].cloneNode(true);
      headClone.setAttribute('aria-hidden', 'true');
      tailClone.setAttribute('aria-hidden', 'true');
      headClone.removeAttribute('id');
      tailClone.removeAttribute('id');
      track.insertBefore(headClone, realSlides[0]);
      track.appendChild(tailClone);
      slides = Array.prototype.slice.call(track.querySelectorAll('.swiper-slide'));
      offset = 1; // DOM index = real index + 1
    }

    // "current" is always a DOM index into `slides`.
    var current = offset; // start on the first real slide
    var timer = null;
    var settling = false;

    // Pagination bullets — one per REAL slide, click maps to DOM index.
    var bullets = [];
    var pag = root.querySelector('.swiper-pagination');
    if (pag) {
      realSlides.forEach(function (s, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'swiper-pagination-bullet';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', function () { goTo(i + offset, true); });
        pag.appendChild(b);
        bullets.push(b);
      });
    }

    // Prev / next
    var prevBtn = root.querySelector('.swiper-button-prev');
    var nextBtn = root.querySelector('.swiper-button-next');
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1, true); });

    // The progress bar may live inside the slider root or as a sibling within the
    // same section wrapper, so fall back to the parent if it isn't a descendant.
    var progress = root.querySelector('[data-native-progress]') ||
      (root.parentElement && root.parentElement.querySelector('[data-native-progress]'));

    function slideWidth() { return slides[0].getBoundingClientRect().width || track.clientWidth; }

    // Real (bullet-facing) index for a given DOM index, wrapped into range.
    function realIndex(i) {
      var n = realSlides.length;
      return ((i - offset) % n + n) % n;
    }

    function clampNoLoop(i) {
      return Math.max(0, Math.min(i, slides.length - 1));
    }

    function paint() {
      var ri = realIndex(current);
      bullets.forEach(function (b, i) {
        b.classList.toggle('swiper-pagination-bullet-active', i === ri);
      });
      if (progress) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
        if (delay && !reduce) {
          void progress.offsetWidth; // reflow so the transition restarts
          progress.style.transition = 'width ' + delay + 'ms linear';
          progress.style.width = '100%';
        }
      }
    }

    // Instantly (no animation, no scroll event side-effects) center the track
    // on DOM index `i`, without touching `current`'s external effects.
    function snapInstant(i) {
      settling = true;
      track.scrollTo({ left: i * slideWidth(), behavior: 'auto' });
      current = i;
      // Release the settling guard after the scroll event this triggers fires.
      requestAnimationFrame(function () { requestAnimationFrame(function () { settling = false; }); });
    }

    function goTo(i, user) {
      if (loop) {
        current = i; // allow moving into clone territory; corrected on settle
      } else {
        current = clampNoLoop(i);
      }
      track.scrollTo({ left: current * slideWidth(), behavior: 'smooth' });
      paint();
      if (user) restart();
    }

    // After a scroll (autoplay tick, button click, or manual swipe) settles on
    // a clone, jump instantly to the matching real slide so the next move can
    // continue smoothly in the same direction.
    function correctIfOnClone() {
      if (!loop) return;
      if (current === 0) {
        snapInstant(slides.length - 2); // real last slide (DOM index)
      } else if (current === slides.length - 1) {
        snapInstant(1); // real first slide (DOM index)
      }
    }

    function start() {
      if (!delay || reduce || realSlides.length < 2) return;
      stop();
      timer = setInterval(function () { goTo(current + 1); }, delay);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function restart() { stop(); start(); }

    // Keep `current` in sync when the user swipes/scrolls manually, and
    // correct off-the-end clone positions once the scroll settles.
    var t;
    track.addEventListener('scroll', function () {
      if (settling) return;
      clearTimeout(t);
      t = setTimeout(function () {
        var i = Math.round(track.scrollLeft / slideWidth());
        if (loop) {
          if (i !== current) { current = i; paint(); }
          correctIfOnClone();
        } else {
          i = clampNoLoop(i);
          if (i !== current) { current = i; paint(); }
        }
      }, 120);
    }, { passive: true });

    // Pause on interaction and when the tab is hidden.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('touchstart', stop, { passive: true });
    root.addEventListener('touchend', restart, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    // Position on the first real slide before anything is painted (no
    // animation on load).
    if (loop) snapInstant(offset);
    paint();
    start();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-native-slider]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();